import React, { useEffect, useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChalkboardUser, faUsers, faDumbbell, faRobot, faUsersGear, faEnvelopeOpenText,
  faRotateRight, faChevronRight, faChevronDown, faTriangleExclamation, faCircleCheck,
  faCalendarXmark, faCreditCard, faUserPlus, faBuilding, faChartLine, faFire,
  faSackDollar, faRankingStar, faUserClock, faUserSlash, faArrowsRotate, faBell,
} from "@fortawesome/free-solid-svg-icons";
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement,
  LineElement, Tooltip, Legend, Filler,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import api from "../services/api";
import styles from "./AdminDashboard.module.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Tooltip, Legend, Filler);

// Cores dos gráficos: fixas (não seguem a cor do professor) e validadas pra
// daltonismo e contraste nos temas claro e escuro (skill dataviz,
// validate_palette.js). Ordem fixa: cada tipo de cadastro sempre na mesma cor.
const SERIES_COLORS = {
  aluno_professor: "#1d9e75",
  aluno_self: "#3a8ed6",
  professor: "#c98418",
};
const ACCENT = "#1d9e75";

// Chart.js desenha em canvas e não entende var(--x): resolve o token pra cor real.
function cssColor(token, fallback) {
  try {
    const el = document.createElement("span");
    el.style.color = `var(${token})`;
    el.style.display = "none";
    document.body.appendChild(el);
    const c = getComputedStyle(el).color;
    el.remove();
    return c || fallback;
  } catch {
    return fallback;
  }
}

function weekLabel(iso) {
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
}

function monthLabel(ym) {
  const [y, m] = ym.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("pt-BR", { month: "short" }).replace(".", "");
}

// Rótulos de valor dos gráficos do admin (plugin local, só nestes gráficos):
// "stackTotal" = só o total em cima de cada pilha (rótulo por segmento se
// sobrepunha); "bars" = valor em cima de cada barra; "points" = valor em
// cada ponto da linha. O plugin global "dataLabels" do analytics do professor
// (TeacherAnalyticsDashboard) fica desligado aqui via options.
const adminLabelsPlugin = {
  id: "adminLabels",
  afterDatasetsDraw(chart, _args, opts) {
    const mode = opts?.mode;
    if (!mode) return;
    const { ctx } = chart;
    const fmt = opts.format || ((v) => String(v));
    ctx.save();
    ctx.fillStyle = cssColor("--muted", "#94a3b8");
    ctx.font = "600 11px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    if (mode === "stackTotal") {
      const n = chart.data.labels.length;
      for (let i = 0; i < n; i++) {
        let total = 0;
        let topY = Infinity;
        let x = null;
        chart.data.datasets.forEach((ds, di) => {
          const meta = chart.getDatasetMeta(di);
          if (meta.hidden) return;
          total += Number(ds.data[i] || 0);
          const el = meta.data[i];
          if (el) { topY = Math.min(topY, el.y); x = el.x; }
        });
        if (total > 0 && x !== null) ctx.fillText(fmt(total), x, topY - 4);
      }
    } else if (mode === "bars") {
      const meta = chart.getDatasetMeta(0);
      meta.data.forEach((el, i) => {
        const v = chart.data.datasets[0].data[i];
        if (v) ctx.fillText(fmt(v), el.x, el.y - 4);
      });
    } else if (mode === "points") {
      // Primeiro e último alinhados pra dentro, senão saem da área do gráfico
      const meta = chart.getDatasetMeta(0);
      const last = meta.data.length - 1;
      meta.data.forEach((el, i) => {
        const v = chart.data.datasets[0].data[i];
        if (v == null) return;
        ctx.textAlign = i === 0 ? "left" : i === last ? "right" : "center";
        ctx.fillText(fmt(v), el.x, el.y - 8);
      });
    }
    ctx.restore();
  },
};

function chartOptions({ stacked = false, money = false, legend = false, lastIsPartial = false, labels = null } = {}) {
  const muted = cssColor("--muted", "#94a3b8");
  const grid = cssColor("--border-0-06", "rgba(148,163,184,0.12)");
  const fmt = (v) => (money ? Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : v);
  const money0 = (v) => Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    // Espaço no topo pros rótulos não encostarem na borda do gráfico
    layout: { padding: { top: labels ? 18 : 4, right: 6 } },
    plugins: {
      dataLabels: false,
      adminLabels: labels ? { mode: labels, format: money ? money0 : undefined } : false,
      legend: legend
        ? { display: true, position: "bottom", labels: { color: muted, boxWidth: 10, boxHeight: 10, usePointStyle: true, font: { size: 11 } } }
        : { display: false },
      tooltip: {
        callbacks: {
          title: (items) => {
            const t = items[0]?.label || "";
            const last = items[0] && items[0].dataIndex === items[0].dataset.data.length - 1;
            return lastIsPartial && last ? `${t} (semana atual, em andamento)` : t;
          },
          label: (ctx) => ` ${ctx.dataset.label}: ${fmt(ctx.parsed.y)}`,
        },
      },
    },
    scales: {
      x: { stacked, grid: { display: false }, ticks: { color: muted, font: { size: 10 }, maxRotation: 0, autoSkip: true } },
      y: {
        stacked, beginAtZero: true, grid: { color: grid }, border: { display: false },
        ticks: { color: muted, font: { size: 10 }, precision: 0, maxTicksLimit: 4, callback: (v) => fmt(v) },
      },
    },
  };
}

// Semana atual (em andamento) num tom mais claro da mesma cor, pra não ler
// como queda o que ainda não terminou.
function partialColors(n, color) {
  return Array.from({ length: n }, (_, i) => (i === n - 1 ? `${color}80` : color));
}

function ChartBox({ title, summary, children }) {
  return (
    <figure className={styles.chartBox}>
      <figcaption className={styles.chartTitle}>{title}</figcaption>
      <div className={styles.chartCanvas} role="img" aria-label={`${title}. ${summary}`}>
        {children}
      </div>
    </figure>
  );
}

// Painel do admin: o dia a dia da plataforma (crescimento, engajamento,
// receita e professores/academias). Alertas operacionais ficam resumidos
// numa linha no rodapé e abrem a lista ao clicar.

const ORG_STATUS_LABEL = {
  em_dia: "em dia",
  carencia: "em carência",
  bloqueada: "bloqueadas",
  cortesia: "cortesia",
};

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("pt-BR");
  } catch {
    return iso;
  }
}

function daysAgo(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const day = new Date(d);
  day.setHours(0, 0, 0, 0);
  return Math.round((start - day) / 86400000);
}

function fmtAgo(iso) {
  const n = daysAgo(iso);
  if (n === null) return "";
  if (n <= 0) return "hoje";
  if (n === 1) return "ontem";
  return `há ${n} dias`;
}

function fmtMoney(v) {
  return Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

// ── Peças ─────────────────────────────────────────────────────────────────────
function Trend({ cur, prev }) {
  if (!prev && !cur) return null;
  if (!prev) return <span className={styles.trendUp}>novo</span>;
  const pct = Math.round(((cur - prev) / prev) * 100);
  if (pct === 0) return <span className={styles.trendFlat}>= 7 dias anteriores</span>;
  return (
    <span className={pct > 0 ? styles.trendUp : styles.trendDown}>
      {pct > 0 ? "▲" : "▼"} {Math.abs(pct)}% vs 7 dias anteriores
    </span>
  );
}

function Kpi({ icon, label, value, sub, trend }) {
  return (
    <div className={styles.scorecard}>
      <div className={styles.scorecardIcon}><FontAwesomeIcon icon={icon} /></div>
      <div className={styles.scorecardBody}>
        <div className={styles.scorecardValue}>{value ?? "-"}</div>
        <div className={styles.scorecardLabel}>{label}</div>
        {sub && <div className={styles.scorecardSub}>{sub}</div>}
        {trend && <div className={styles.scorecardTrend}>{trend}</div>}
      </div>
    </div>
  );
}

function Panel({ icon, title, hint, action, wide = false, children }) {
  return (
    <section className={`${styles.panel} ${wide ? styles.panelWide : ""}`}>
      <header className={styles.panelHeader}>
        <h3 className={styles.panelTitle}><FontAwesomeIcon icon={icon} /> {title}</h3>
        {action}
      </header>
      {hint && <p className={styles.panelHint}>{hint}</p>}
      {children}
    </section>
  );
}

function MiniList({ title, count, empty, items, render }) {
  return (
    <div className={styles.miniList}>
      <div className={styles.miniListTitle}>
        <span>{title}</span>
        {count > 0 && <span className={styles.miniListCount}>{count}</span>}
      </div>
      {items.length === 0 ? (
        <p className={styles.miniEmpty}>{empty}</p>
      ) : (
        <ul className={styles.rows}>
          {items.map((it) => (
            <li key={`${it.kind || "u"}-${it.id}`} className={styles.row}>{render(it)}</li>
          ))}
        </ul>
      )}
      {count > items.length && (
        <p className={styles.miniMore}>e mais {count - items.length}</p>
      )}
    </div>
  );
}

function AlertGroup({ title, icon, severity, count, children }) {
  return (
    <div className={`${styles.alertGroup} ${styles[`alertGroup_${severity}`]}`}>
      <div className={styles.alertGroupHeader}>
        <FontAwesomeIcon icon={icon} className={styles.alertGroupIcon} />
        <span className={styles.alertGroupTitle}>{title}</span>
        <span className={styles.alertGroupCount}>{count}</span>
      </div>
      <div className={styles.alertRows}>{children}</div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
// ── Retenção por turma e ativação ─────────────────────────────────────────────
const COHORT_COLS = 8;

function fmtDays(v) {
  if (v == null) return "-";
  const n = Number(v);
  if (n < 1) return "menos de 1 dia";
  const txt = n.toLocaleString("pt-BR", { maximumFractionDigits: 1 });
  return `${txt} ${n === 1 ? "dia" : "dias"}`;
}

function PointsTrend({ cur, prev }) {
  if (cur == null || prev == null) return null;
  const diff = cur - prev;
  if (diff === 0) return <span className={styles.trendFlat}>= 30 dias anteriores</span>;
  return (
    <span className={diff > 0 ? styles.trendUp : styles.trendDown}>
      {diff > 0 ? "▲" : "▼"} {Math.abs(diff)} p.p. vs 30 dias anteriores
    </span>
  );
}

// Escala de uma cor só (o tom de destaque, mais forte = mais gente treinando).
// Opacidade limitada a 0.6 pra o texto (cor de texto do tema) seguir legível
// nos dois temas; o número na célula carrega o valor, a cor só reforça.
function cellStyle(pct) {
  if (pct == null) return undefined;
  const a = 0.06 + 0.54 * Math.min(pct, 100) / 100;
  return { background: `rgba(29, 158, 117, ${a.toFixed(2)})` };
}

function RetentionPanel({ ret, noFirstCount }) {
  const cohorts = ret?.cohorts || [];
  const act = ret?.activation || {};
  const best = cohorts.reduce((b, c) => (c.size > (b?.size || 0) ? c : b), null);
  const summary = best
    ? `Maior turma: ${weekLabel(best.week)}, ${best.size} alunos; na semana atual ${best.pct[best.pct.length - 1] ?? 0}% deles treinaram.`
    : "Sem cadastros no período.";

  return (
    <Panel icon={faArrowsRotate} title="Retenção e ativação" wide
           hint="Se quem entra começa a treinar e continua treinando">
      <div className={styles.retStats}>
        <div className={styles.retStat}>
          <span className={styles.retStatLabel}>Ativação em {act.days || 7} dias</span>
          <span className={styles.retStatValue}>{act.rate == null ? "-" : `${act.rate}%`}</span>
          <span className={styles.retStatSub}>
            {act.cohort_size
              ? `${act.activated} de ${act.cohort_size} alunos que entraram entre ${fmtDate(act.start)} e ${fmtDate(act.end)} fizeram o 1º treino`
              : "Nenhum aluno entrou no período"}
          </span>
          <span className={styles.retStatTrend}><PointsTrend cur={act.rate} prev={act.prev_rate} /></span>
        </div>
        <div className={styles.retStat}>
          <span className={styles.retStatLabel}>Tempo até o 1º treino</span>
          <span className={styles.retStatValue}>{fmtDays(act.median_days)}</span>
          <span className={styles.retStatSub}>mediana, entre os que ativaram</span>
        </div>
        <div className={styles.retStat}>
          <span className={styles.retStatLabel}>Ainda sem 1º treino</span>
          <span className={styles.retStatValue}>{noFirstCount}</span>
          <span className={styles.retStatSub}>alunos cadastrados nos últimos 14 dias</span>
        </div>
      </div>

      <div className={styles.cohortScroll}>
        <table className={styles.cohortTable} aria-describedby="cohort-note">
          <caption className={styles.srOnly}>
            Retenção por turma de cadastro. {summary}
          </caption>
          <thead>
            <tr>
              <th scope="col" className={styles.cohortHead}>Turma</th>
              <th scope="col" className={styles.cohortNum}>Alunos</th>
              {Array.from({ length: COHORT_COLS }, (_, k) => (
                <th key={k} scope="col" className={styles.cohortNum}>Sem. {k}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...cohorts].reverse().map((c) => (
              <tr key={c.week}>
                <th scope="row" className={styles.cohortHead}>{weekLabel(c.week)}</th>
                <td className={styles.cohortNum}>{c.size}</td>
                {Array.from({ length: COHORT_COLS }, (_, k) => {
                  if (k >= c.pct.length) return <td key={k} className={styles.cohortFuture} aria-hidden="true" />;
                  if (!c.size) return <td key={k} className={styles.cohortNum}>-</td>;
                  const current = k === c.pct.length - 1;
                  return (
                    <td
                      key={k}
                      className={`${styles.cohortCell} ${current ? styles.cohortCurrent : ""}`}
                      style={cellStyle(c.pct[k])}
                      title={`${c.active[k]} de ${c.size} alunos treinaram${current ? " (semana em andamento)" : ""}`}
                    >
                      {c.pct[k]}%
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p id="cohort-note" className={styles.cohortNote}>
        Cada linha é a turma que se cadastrou naquela semana. Sem. 0 é a semana do cadastro; a célula com borda é a semana atual, ainda em andamento.
      </p>
    </Panel>
  );
}

export default function AdminDashboard({ onOpenSendEmail, onOpenSendPush, onOpenAiUsage, onOpenManageUsers, onOpenPaymentsAdmin, onOpenInviteProfessor, onOpenOrganizations }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alertsOpen, setAlertsOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/dashboard");
      setData(res.data);
    } catch (e) {
      console.error("AdminDashboard load error:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className={styles.loadingWrap}>
        <FontAwesomeIcon icon={faRotateRight} spin />
        <span>Carregando painel administrativo...</span>
      </div>
    );
  }

  const growth = data?.growth || { today: 0, week: 0, prev_week: 0, week_by_type: {}, recent: [] };
  const eng = data?.engagement || { today: 0, week: 0, prev_week: 0, students_active: 0, no_first_workout: { count: 0, items: [] }, stopped: { count: 0, items: [] } };
  const rev = data?.revenue || { mrr: 0, subscriptions: {}, paid_30d: 0, pending: {}, organizations: {} };
  const net = data?.network || { ranking: [], idle: { count: 0, items: [] }, new_without_students: { count: 0, items: [] } };

  const alerts = data?.alerts || {};
  const plansExpiring = alerts.plans_expiring_soon || [];
  const invitesExpiring = alerts.invites_expiring_soon || [];
  const bounced = alerts.email_bounced || [];
  const totalAlerts = plansExpiring.length + invitesExpiring.length + bounced.length;
  const ai = data?.ai_usage_7d || { calls: 0, cost_usd: 0, error_rate_pct: 0 };

  const seriesWeeks = data?.series?.weeks || [];
  const revenueMonths = data?.series?.revenue_by_month || [];
  const weekLabels = seriesWeeks.map((w) => weekLabel(w.week));
  const firstWeek = seriesWeeks[0];
  const lastWeek = seriesWeeks.length
    ? { ...seriesWeeks[seriesWeeks.length - 1],
        signupsTotal: Object.values(seriesWeeks[seriesWeeks.length - 1].signups).reduce((a, b) => a + b, 0) }
    : null;

  const activePct = eng.students_active ? Math.round((eng.week / eng.students_active) * 100) : 0;
  const subsTotal = Object.values(rev.subscriptions || {}).reduce((a, b) => a + b, 0);
  const orgsTotal = Object.values(rev.organizations || {}).reduce((a, b) => a + b, 0);
  const pendingTotal = (rev.pending?.overdue_payments || 0) + (rev.pending?.past_due_subscriptions || 0);

  const alertParts = [
    plansExpiring.length > 0 && `${plansExpiring.length} ${plansExpiring.length === 1 ? "plano vencendo" : "planos vencendo"}`,
    invitesExpiring.length > 0 && `${invitesExpiring.length} ${invitesExpiring.length === 1 ? "convite expirando" : "convites expirando"}`,
    bounced.length > 0 && `${bounced.length} ${bounced.length === 1 ? "email com falha" : "emails com falha"}`,
  ].filter(Boolean);

  return (
    <div className={styles.root}>
      {/* ── Atalhos rápidos ── */}
      <div className={styles.shortcuts}>
        <button className={styles.shortcutBtn} onClick={onOpenSendEmail}>
          <FontAwesomeIcon icon={faEnvelopeOpenText} /> Enviar email
        </button>
        {onOpenSendPush && (
          <button className={styles.shortcutBtn} onClick={onOpenSendPush}>
            <FontAwesomeIcon icon={faBell} /> Enviar notificação
          </button>
        )}
        <button className={styles.shortcutBtn} onClick={onOpenManageUsers}>
          <FontAwesomeIcon icon={faUsersGear} /> Gerenciar usuários
        </button>
        <button className={styles.shortcutBtn} onClick={onOpenAiUsage}>
          <FontAwesomeIcon icon={faRobot} /> Consumo de IA
        </button>
        <button className={styles.shortcutBtn} onClick={onOpenPaymentsAdmin}>
          <FontAwesomeIcon icon={faCreditCard} /> Gestão de pagamentos
        </button>
        <button className={styles.shortcutBtn} onClick={onOpenInviteProfessor}>
          <FontAwesomeIcon icon={faUserPlus} /> Convidar professor
        </button>
        {onOpenOrganizations && (
          <button className={styles.shortcutBtn} onClick={onOpenOrganizations}>
            <FontAwesomeIcon icon={faBuilding} /> Academias
          </button>
        )}
        <button className={styles.refreshBtn} onClick={load} title="Atualizar" aria-label="Atualizar painel">
          <FontAwesomeIcon icon={faRotateRight} />
        </button>
      </div>

      {/* ── Números do dia ── */}
      <div className={styles.scorecards}>
        <Kpi icon={faUserPlus} label="Cadastros em 7 dias" value={growth.week}
             sub={`${growth.today} hoje`} trend={<Trend cur={growth.week} prev={growth.prev_week} />} />
        {/* Janela móvel de 7 dias (hoje + 6 anteriores), como os cadastros; os
            gráficos abaixo é que usam a semana de calendário (seg a dom). */}
        <Kpi icon={faFire} label="Treinaram em 7 dias" value={eng.week}
             sub={`${activePct}% dos ${eng.students_active} alunos ativos`} trend={<Trend cur={eng.week} prev={eng.prev_week} />} />
        <Kpi icon={faDumbbell} label="Treinaram hoje" value={eng.today} />
        <Kpi icon={faSackDollar} label="Receita mensal (app)" value={fmtMoney(rev.mrr)}
             sub={`${subsTotal} ${subsTotal === 1 ? "assinatura ativa" : "assinaturas ativas"}`} />
        <Kpi icon={faUsers} label="Alunos" value={data?.students?.total}
             sub={`${data?.students?.with_professor ?? 0} com professor · ${data?.students?.self_service ?? 0} self-service`} />
        <Kpi icon={faChalkboardUser} label="Professores ativos" value={data?.professors?.active}
             sub={`${orgsTotal} ${orgsTotal === 1 ? "academia" : "academias"}`} />
      </div>

      <div className={styles.panels}>
        {/* ── Crescimento ── */}
        <Panel icon={faChartLine} title="Crescimento" hint="Últimas 12 semanas">
          <ChartBox
            title="Cadastros por semana"
            summary={`Esta semana: ${lastWeek?.signupsTotal ?? 0} cadastros.`}
          >
            <Bar
              data={{
                labels: weekLabels,
                datasets: [
                  { key: "aluno_professor", label: "Alunos de professor" },
                  { key: "aluno_self", label: "Self-service" },
                  { key: "professor", label: "Professores" },
                ].map(({ key, label }) => ({
                  label,
                  data: seriesWeeks.map((w) => w.signups[key]),
                  backgroundColor: SERIES_COLORS[key],
                  borderRadius: 4,
                  borderSkipped: "start",
                  maxBarThickness: 28,
                })),
              }}
              options={chartOptions({ stacked: true, legend: true, lastIsPartial: true, labels: "stackTotal" })}
              plugins={[adminLabelsPlugin]}
            />
          </ChartBox>
          <ChartBox
            title="Total de usuários"
            summary={`${lastWeek?.total_users ?? 0} usuários hoje, ${firstWeek?.total_users ?? 0} há 12 semanas.`}
          >
            <Line
              data={{
                labels: weekLabels,
                datasets: [{
                  label: "Usuários",
                  data: seriesWeeks.map((w) => w.total_users),
                  borderColor: ACCENT,
                  backgroundColor: `${ACCENT}22`,
                  fill: true,
                  borderWidth: 2,
                  pointRadius: 0,
                  pointHoverRadius: 4,
                  tension: 0.25,
                }],
              }}
              options={chartOptions({ labels: "points" })}
              plugins={[adminLabelsPlugin]}
            />
          </ChartBox>
        </Panel>

        {/* ── Engajamento ── */}
        <Panel icon={faFire} title="Engajamento" hint="Alunos e treinos por semana, últimas 12 semanas">
          <ChartBox
            title="Alunos que treinaram"
            summary={`Esta semana: ${lastWeek?.active_students ?? 0} alunos.`}
          >
            <Bar
              data={{
                labels: weekLabels,
                datasets: [{
                  label: "Alunos",
                  data: seriesWeeks.map((w) => w.active_students),
                  backgroundColor: partialColors(seriesWeeks.length, ACCENT),
                  borderRadius: 4,
                  borderSkipped: "start",
                  maxBarThickness: 28,
                }],
              }}
              options={chartOptions({ lastIsPartial: true, labels: "bars" })}
              plugins={[adminLabelsPlugin]}
            />
          </ChartBox>
          <ChartBox
            title="Treinos registrados"
            summary={`Esta semana: ${lastWeek?.workouts ?? 0} treinos.`}
          >
            <Bar
              data={{
                labels: weekLabels,
                datasets: [{
                  label: "Treinos",
                  data: seriesWeeks.map((w) => w.workouts),
                  backgroundColor: partialColors(seriesWeeks.length, SERIES_COLORS.aluno_self),
                  borderRadius: 4,
                  borderSkipped: "start",
                  maxBarThickness: 28,
                }],
              }}
              options={chartOptions({ lastIsPartial: true, labels: "bars" })}
              plugins={[adminLabelsPlugin]}
            />
          </ChartBox>
        </Panel>

        {/* ── Retenção e ativação (largura total) ── */}
        <RetentionPanel ret={data?.retention} noFirstCount={eng.no_first_workout?.count || 0} />

        {/* ── Receita ── */}
        <Panel
          icon={faSackDollar}
          title="Receita"
          hint="Assinaturas e pagamentos feitos pelo app (contratos fora do app não entram)"
          action={
            <button type="button" className={styles.panelLink} onClick={onOpenPaymentsAdmin}>
              Pagamentos <FontAwesomeIcon icon={faChevronRight} />
            </button>
          }
        >
          <div className={styles.statGrid}>
            <div className={styles.stat}>
              <span className={styles.statValue}>{fmtMoney(rev.mrr)}</span>
              <span className={styles.statLabel}>receita mensal recorrente</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statValue}>{fmtMoney(rev.paid_30d)}</span>
              <span className={styles.statLabel}>recebido em 30 dias</span>
            </div>
            <div className={styles.stat}>
              <span className={`${styles.statValue} ${pendingTotal > 0 ? styles.statWarn : ""}`}>{pendingTotal}</span>
              <span className={styles.statLabel}>cobranças vencidas ou atrasadas</span>
            </div>
          </div>
          <ChartBox
            title="Recebido por mês"
            summary={`Últimos 6 meses: ${fmtMoney(revenueMonths.reduce((a, m) => a + m.amount, 0))}.`}
          >
            <Bar
              data={{
                labels: revenueMonths.map((m) => monthLabel(m.month)),
                datasets: [{
                  label: "Recebido",
                  data: revenueMonths.map((m) => m.amount),
                  backgroundColor: ACCENT,
                  borderRadius: 4,
                  borderSkipped: "start",
                  maxBarThickness: 32,
                }],
              }}
              options={chartOptions({ money: true, labels: "bars" })}
              plugins={[adminLabelsPlugin]}
            />
          </ChartBox>
          <ul className={styles.rows}>
            <li className={styles.row}>
              <span className={styles.rowName}>Assinaturas Plus</span>
              <span className={styles.rowMeta}>{rev.subscriptions?.aluno || 0}</span>
            </li>
            <li className={styles.row}>
              <span className={styles.rowName}>Assinaturas de professor</span>
              <span className={styles.rowMeta}>{rev.subscriptions?.professor || 0}</span>
            </li>
            <li className={styles.row}>
              <span className={styles.rowName}>Assinaturas de academia</span>
              <span className={styles.rowMeta}>{rev.subscriptions?.academia || 0}</span>
            </li>
            <li className={styles.row}>
              <span className={styles.rowName}>Academias</span>
              <span className={styles.rowMeta}>
                {orgsTotal === 0
                  ? "nenhuma"
                  : Object.entries(rev.organizations || {})
                      .map(([k, v]) => `${v} ${ORG_STATUS_LABEL[k] || k}`)
                      .join(" · ")}
              </span>
            </li>
          </ul>
        </Panel>

        {/* ── Professores e academias ── */}
        <Panel icon={faRankingStar} title="Professores e academias" hint="Alunos que treinaram nos últimos 7 dias">
          {net.concentration?.top && (
            <p className={styles.concentration}>
              <strong>{net.concentration.top.share_pct}%</strong> dos {net.concentration.active_week_total} alunos que
              treinaram vêm de <strong>{net.concentration.top.name}</strong>
              {net.concentration.self_service_pct > 0 && <> · {net.concentration.self_service_pct}% treinam por conta própria</>}
              {net.concentration.top.share_pct >= 60 && (
                <span className={styles.concentrationWarn}>
                  <FontAwesomeIcon icon={faTriangleExclamation} aria-hidden="true" /> concentração alta
                </span>
              )}
            </p>
          )}
          {net.ranking?.length ? (
            <ul className={styles.rows}>
              {net.ranking.map((u) => {
                const pct = u.students ? Math.round((u.active_week / u.students) * 100) : 0;
                return (
                  <li key={`${u.kind}-${u.id}`} className={styles.row}>
                    <span className={styles.rowMain}>
                      <span className={styles.rowName}>
                        {u.name}
                        {u.kind === "academia" && <span className={styles.badge}>Academia</span>}
                      </span>
                      <span className={styles.bar} aria-hidden="true"><span style={{ width: `${pct}%` }} /></span>
                    </span>
                    <span className={styles.rowMeta}>{u.active_week} de {u.students}</span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className={styles.miniEmpty}>Nenhum professor com alunos ainda.</p>
          )}
          <MiniList
            title="Sem alunos treinando há 14 dias"
            count={net.idle?.count || 0}
            empty="Todos com alunos treinando."
            items={net.idle?.items || []}
            render={(u) => (
              <>
                <span className={styles.rowMain}>
                  <span className={styles.rowName}><FontAwesomeIcon icon={faUserSlash} className={styles.rowIcon} /> {u.name}</span>
                </span>
                <span className={styles.rowMeta}>{u.students} alunos</span>
              </>
            )}
          />
          <MiniList
            title="Professores novos sem alunos"
            count={net.new_without_students?.count || 0}
            empty="Todos os professores novos já têm alunos."
            items={net.new_without_students?.items || []}
            render={(u) => (
              <>
                <span className={styles.rowMain}>
                  <span className={styles.rowName}><FontAwesomeIcon icon={faUserClock} className={styles.rowIcon} /> {u.name}</span>
                </span>
                <span className={styles.rowMeta}>entrou {fmtAgo(u.created_at)}</span>
              </>
            )}
          />
        </Panel>
      </div>

      {/* ── Rodapé: alertas operacionais + IA (resumidos) ── */}
      <div className={styles.footerBar}>
        <button
          type="button"
          className={styles.footerAlerts}
          onClick={() => setAlertsOpen((v) => !v)}
          aria-expanded={alertsOpen}
          disabled={totalAlerts === 0}
        >
          <FontAwesomeIcon icon={totalAlerts > 0 ? faTriangleExclamation : faCircleCheck}
                           className={totalAlerts > 0 ? styles.footerWarnIcon : styles.footerOkIcon} />
          <span>{totalAlerts > 0 ? alertParts.join(" · ") : "Nenhum alerta operacional"}</span>
          {totalAlerts > 0 && <FontAwesomeIcon icon={faChevronDown} className={alertsOpen ? styles.chevronOpen : styles.chevron} />}
        </button>
        <button type="button" className={styles.footerAi} onClick={onOpenAiUsage}>
          <FontAwesomeIcon icon={faRobot} />
          <span>
            IA em 7 dias: {ai.calls} {ai.calls === 1 ? "chamada" : "chamadas"} · ${ai.cost_usd.toFixed(2)} ·{" "}
            <span className={ai.error_rate_pct > 5 ? styles.aiErrorHigh : undefined}>{ai.error_rate_pct}% erro</span>
          </span>
          <FontAwesomeIcon icon={faChevronRight} className={styles.chevron} />
        </button>
      </div>

      {alertsOpen && totalAlerts > 0 && (
        <div className={styles.alertGroupsWrap}>
          {plansExpiring.length > 0 && (
            <AlertGroup title="Planos vencendo" icon={faCalendarXmark} severity="amber" count={plansExpiring.length}>
              {plansExpiring.map((p) => (
                <div key={p.id} className={styles.alertRow}>
                  <span className={styles.alertName}>{p.name}</span>
                  <span className={styles.alertMeta}>
                    {p.expired ? "venceu em" : "vence em"} {fmtDate(p.plan_expires_at)}
                  </span>
                </div>
              ))}
            </AlertGroup>
          )}
          {invitesExpiring.length > 0 && (
            <AlertGroup title="Convites expirando" icon={faEnvelopeOpenText} severity="amber" count={invitesExpiring.length}>
              {invitesExpiring.map((inv) => (
                <div key={inv.id} className={styles.alertRow}>
                  <span className={styles.alertName}>
                    {inv.name} <span className={styles.alertSub}>({inv.email})</span>
                  </span>
                  <span className={styles.alertMeta}>convidado por {inv.professor_name || "-"}</span>
                </div>
              ))}
            </AlertGroup>
          )}
          {bounced.length > 0 && (
            <AlertGroup title="Emails com falha de entrega" icon={faTriangleExclamation} severity="red" count={bounced.length}>
              {bounced.map((u) => (
                <div key={u.id} className={styles.alertRow}>
                  <span className={styles.alertName}>
                    {u.name} <span className={styles.alertSub}>({u.email})</span>
                  </span>
                  <span className={styles.alertMeta} title={u.reason || ""}>{(u.reason || "bounce").split(":")[0]}</span>
                </div>
              ))}
            </AlertGroup>
          )}
        </div>
      )}
    </div>
  );
}
